import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { Pair } from '@uniswap/sdk'
import { Link } from 'react-router-dom'
import { SwapPoolTabs } from '../../components/NavigationTabs'

import FullPositionCard from '../../components/PositionCard'
import { useTokenBalancesWithLoadingIndicator } from '../../state/wallet/hooks'
import { StyledInternalLink, TYPE, HideSmall } from '../../theme'
import { Text } from 'rebass'
import Card from '../../components/Card'
import { RowBetween, RowFixed } from '../../components/Row'
import { ButtonPrimary, ButtonSecondary } from '../../components/Button'
import { AutoColumn } from '../../components/Column'

import { useActiveWeb3React } from '../../hooks'
import { usePairs } from '../../data/Reserves'
import { toV2LiquidityToken, useTrackedTokenPairs } from '../../state/user/hooks'
import { Dots } from '../../components/swap/styleds'
import { CardSection, DataCard, CardNoise, CardBGImage } from '../../components/earn/styled'
import { Contract, ethers } from 'ethers'
import ABI from '../../abis/NewRouterAbi.json'
import tokensNew from '../../tokens.json'
const PageWrapper = styled(AutoColumn)`
  max-width: 640px;
  width: 100%;
`

const VoteCard = styled(DataCard)`
  background: radial-gradient(76.02% 75.41% at 1.84% 0%, #27ae60 0%, #000000 100%);
  overflow: hidden;
`

const TitleRow = styled(RowBetween)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
    flex-direction: column-reverse;
  `};
`

const ButtonRow = styled(RowFixed)`
  gap: 8px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
  `};
`

const ResponsiveButtonPrimary = styled(ButtonPrimary)`
  width: fit-content;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 48%;
  `};
`

const ResponsiveButtonSecondary = styled(ButtonSecondary)`
  width: fit-content;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 48%;
  `};
`

const EmptyProposals = styled.div`
  border: 1px solid ${({ theme }) => theme.text4};
  padding: 16px 12px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

export default function Pool() {
  const theme = useContext(ThemeContext)
  const { account } = useActiveWeb3React()

  // fetch the user's balances of all tracked V2 LP tokens
  const trackedTokenPairs = useTrackedTokenPairs()

  const tokenPairsWithLiquidityTokens = useMemo(
    () => trackedTokenPairs.map((tokens) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
    [trackedTokenPairs]
  )

  const liquidityTokens = useMemo(
    () => tokenPairsWithLiquidityTokens.map((tpwlt) => tpwlt.liquidityToken),
    [tokenPairsWithLiquidityTokens]
  )

  const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    liquidityTokens
  )

  // fetch the reserves for all V2 pools in which the user has a balance
  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
        v2PairsBalances[liquidityToken.address]?.greaterThan('0')
      ),
    [tokenPairsWithLiquidityTokens, v2PairsBalances]
  )

  const v2Pairs = usePairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
  const v2IsLoading =
    fetchingV2PairBalances || v2Pairs?.length < liquidityTokensWithBalances.length || v2Pairs?.some((V2Pair) => !V2Pair)
  console.log('v2IsLoading', v2IsLoading)
  const allV2PairsWithLiquidity = v2Pairs.map(([, pair]) => pair).filter((v2Pair): v2Pair is Pair => Boolean(v2Pair))
  // console.log(allV2PairsWithLiquidity, "allV2PairsWithLiquidity")

  const [tokens, setTokens] = useState<any>([])
  const [loading, setLoading] = useState(true)
  const [pools, setPools] = useState([])

  const Cropto_Router = '0xc9477AEd020b3786E68aCFe38770c396F0070EaE'
  const USDT_ADDRESS = '0xc2132D05D31c914a87C6611C10748AEb04B58e8F'

  const load = async (account: string, setPools: Function, setLoading: Function, setTokens: Function) => {
    try {
      const provider = new ethers.providers.JsonRpcProvider(
        'https://polygon-mainnet.g.alchemy.com/v2/THjT9IccqPam8aUXz90I52OMXk3-j9qz'
      )
      const contract = new Contract(Cropto_Router, ABI, provider)

      const result = await contract.getPools()

      const [
        creators,
        poolAddresses,
        token0s,
        token1s,
        amount0s,
        amount1s,
        statuses,
        timestamps,
        firstInAmounts,
        firstOutAmounts,
      ] = result

      const formattedPools = poolAddresses.map((pool: any, index: any) => {
        const token0 = token0s[index]
        const token1 = token1s[index]

        // USDT'yi her zaman token0 olarak ayarla
        const tokenPair = { token0, token1 }

        return {
          poolAddress: pool,
          ...tokenPair,
          amount0: BigInt(amount0s[index] || 0),
          amount1: BigInt(amount1s[index] || 0),
          firstAmount0: BigInt(firstInAmounts[index] || 0),
          firstAmount1: BigInt(firstOutAmounts[index] || 0),
          status: statuses[index],
          timeStamp: BigInt(timestamps[index] || 0),
          creator: creators[index],
          amount0String: amount0s[index]?.toString() || '0',
          amount1String: amount1s[index]?.toString() || '0',
          timestampISO: new Date(Number(timestamps[index]) * 1000).toISOString(),
        }
      })

      // Sadece giriş yapan kullanıcının oluşturduğu havuzlar
      const userPools = formattedPools.filter((pool: any) => pool.creator.toLowerCase() === account.toLowerCase())

      // Aynı token çiftlerine ait havuzları birleştir
      const combinedPools = Object.values(
        userPools.reduce((acc: any, pool: any) => {
          const key = [pool.token0, pool.token1].sort().join('-')
          if (!acc[key]) {
            acc[key] = { ...pool }
          } else {
            acc[key].amount0 += pool.amount0
            acc[key].amount1 += pool.amount1
            acc[key].firstAmount0 += pool.firstAmount0
            acc[key].firstAmount1 += pool.firstAmount1

            acc[key].amount0String = acc[key].amount0.toString()
            acc[key].amount1String = acc[key].amount1.toString()
          }
          return acc
        }, {} as Record<string, (typeof formattedPools)[number]>)
      )

      setPools(combinedPools)
    } catch (error) {
      console.error('Veri yüklenirken hata oluştu:', error)
    } finally {
      setLoading(false)
      setTokens(tokensNew?.tokens || [])
    }
  }

  // Component içinde çağırmak:
  useEffect(() => {
    if (!account) return
    setLoading(true)
    load(account, setPools, setLoading, setTokens)
  }, [account])

  console.log('loading', loading)
  console.log('pools', pools)
  console.log('tokens', tokens)

  return (
    <>
      <PageWrapper>
        <SwapPoolTabs active={'pool'} />

        <VoteCard>
          <CardBGImage />
          <CardNoise />
          <CardSection>
            <AutoColumn gap="md">
              <RowBetween>
                <TYPE.white fontWeight={600}>Liquidity provider rewards</TYPE.white>
              </RowBetween>
              <RowBetween>
                <TYPE.white fontSize={14}>
                  {`Liquidity providers earn a 0.25% fee on all trades proportional to their share of the pool. Fees are added to the pool, accrue in real time and can be claimed by withdrawing your liquidity.`}
                </TYPE.white>
              </RowBetween>
              {/*<ExternalLink
                style={{ color: 'white', textDecoration: 'underline' }}
                target="_blank"
                href="https://uniswap.org/docs/v2/core-concepts/pools/"
              >
                <TYPE.white fontSize={14}>Read more about providing liquidity</TYPE.white>
              </ExternalLink>*/}
            </AutoColumn>
          </CardSection>
          <CardBGImage />
          <CardNoise />
        </VoteCard>

        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow style={{ marginTop: '1rem' }} padding={'0'}>
              <HideSmall>
                <TYPE.mediumHeader style={{ marginTop: '0.5rem', justifySelf: 'flex-start' }}>
                  Your liquidity
                </TYPE.mediumHeader>
              </HideSmall>
              <ButtonRow>
                <ResponsiveButtonSecondary
                  as={Link}
                  padding="6px 8px"
                  to="/create/undefined/0xc2132D05D31c914a87C6611C10748AEb04B58e8F"
                >
                  Create a pair
                </ResponsiveButtonSecondary>
                <ResponsiveButtonPrimary
                  id="join-pool-button"
                  as={Link}
                  padding="6px 8px"
                  to="/add/undefined/0xc2132D05D31c914a87C6611C10748AEb04B58e8F"
                >
                  <Text fontWeight={500} fontSize={16}>
                    Add Liquidity
                  </Text>
                </ResponsiveButtonPrimary>
              </ButtonRow>
            </TitleRow>

            {!account ? (
              <Card padding="40px">
                <TYPE.body color={theme.text3} textAlign="center">
                  Connect to a wallet to view your liquidity.
                </TYPE.body>
              </Card>
            ) : loading ? (
              <EmptyProposals>
                <TYPE.body color={theme.text3} textAlign="center">
                  <Dots>Loading</Dots>
                </TYPE.body>
              </EmptyProposals>
            ) : pools?.length > 0 ? (
              <>
                <div
                  className="table-responsive"
                  style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid gray' }}
                >
                  <div
                    className="table-responsive"
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      overflow: 'auto',
                    }}
                  >
                    <table className="table mb-0" style={{ borderCollapse: 'collapse' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>Token 0</th>
                          <th style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>Token 1</th>
                          <th style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>Amount 0</th>
                          <th style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>Amount 1</th>
                          <th style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>Status</th>
                          <th style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pools?.map((item: any, index: any) => (
                          <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '12px 16px' }}>
                              {USDT_ADDRESS === item.token0
                                ? tokens?.find(
                                    (data: any) => data?.address.toLowerCase() === item?.token1.toLowerCase()
                                  )?.symbol
                                : tokens?.find(
                                    (data: any) => data?.address.toLowerCase() === item?.token0.toLowerCase()
                                  )?.symbol}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {USDT_ADDRESS === item.token1
                                ? tokens?.find(
                                    (data: any) => data?.address.toLowerCase() === item?.token1.toLowerCase()
                                  )?.symbol
                                : tokens?.find(
                                    (data: any) => data?.address.toLowerCase() === item?.token0.toLowerCase()
                                  )?.symbol}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {USDT_ADDRESS === item.token0
                                ? (Number(item.amount1?.toString()) / 1e18).toLocaleString()
                                : (Number(item.amount0?.toString()) / 1e18).toLocaleString()}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {USDT_ADDRESS === item.token0
                                ? (Number(item.amount0?.toString()) / 1e6).toLocaleString()
                                : (Number(item.amount1?.toString()) / 1e6).toLocaleString()}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span className={`badge ${item.status ? 'bg-success' : 'bg-secondary'}`}>
                                {item.status ? 'Active' : 'Passive'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {new Date(Number(item.timeStamp?.toString()) * 1000).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* <ButtonSecondary>
                  <RowBetween>
                    <ExternalLink href={'https://info.quickswap.exchange/account/' + account}>
                      Account analytics and accrued fees
                    </ExternalLink>
                    <span> ↗</span>
                  </RowBetween>
                </ButtonSecondary> */}

                {allV2PairsWithLiquidity.map((v2Pair) => (
                  <FullPositionCard key={v2Pair.liquidityToken.address} pair={v2Pair} />
                ))}
              </>
            ) : (
              <EmptyProposals>
                <TYPE.body color={theme.text3} textAlign="center">
                  No liquidity found.
                </TYPE.body>
              </EmptyProposals>
            )}

            <AutoColumn justify={'center'} gap="md">
              <Text textAlign="center" fontSize={14} style={{ padding: '.5rem 0 .5rem 0' }}>
                Don't see a pool you joined?
                <StyledInternalLink id="import-pool-link" to="/find">
                  Import it.
                </StyledInternalLink>
              </Text>
            </AutoColumn>
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
    </>
  )
}
